import { useState } from 'react';

interface FaqItem {
  question: string;
  answer: string;
}

const faqs: FaqItem[] = [
  {
    question: 'What exactly is BaseTao Spreadsheet?',
    answer: 'BaseTao Spreadsheet is a comprehensive research and organization tool designed for shoppers who want to make informed purchasing decisions. It helps you track products, compare prices, calculate shipping costs, manage warehouse storage, and stay organized throughout your entire shopping journey. Think of it as your personal shopping command center that brings clarity and structure to every purchase.',
  },
  {
    question: 'Is BaseTao Spreadsheet free to use?',
    answer: 'BaseTao Spreadsheet offers flexible access options to suit different needs. While the core spreadsheet templates and research tools are available at no cost, premium features including advanced shipping calculators, priority warehouse management, and exclusive coupon aggregators may require a subscription. The free tier already provides substantial value for most casual shoppers.',
  },
  {
    question: 'How does BaseTao Spreadsheet help me save on shipping costs?',
    answer: 'BaseTao Spreadsheet includes a built-in shipping cost analyzer that compares rates across DHL, EMS, FedEx, and other carriers in real time. By organizing your purchases and consolidating parcels strategically, the spreadsheet helps you identify the most cost-effective shipping combinations — potentially saving 20-40% on international shipping fees compared to shipping items individually.',
  },
  {
    question: 'Can I use BaseTao Spreadsheet on my mobile device?',
    answer: 'Absolutely! BaseTao Spreadsheet is fully responsive and works seamlessly across desktop, tablet, and mobile devices. The mobile-optimized interface ensures you can check your product research, update tracking information, and access coupon codes on the go — whether you are at home, in the office, or traveling. All data syncs in real time across your devices.',
  },
  {
    question: 'How secure is my data on BaseTao Spreadsheet?',
    answer: 'Security is a top priority for BaseTao Spreadsheet. All your research data, purchase history, and personal information are protected with enterprise-grade encryption both in transit and at rest. The platform follows strict data protection protocols and never shares your information with third parties. Regular security audits ensure your data remains safe and private at all times.',
  },
  {
    question: 'What makes BaseTao Spreadsheet different from just using a regular spreadsheet?',
    answer: 'While a regular spreadsheet can technically store data, BaseTao Spreadsheet is purpose-built for shopping research with pre-configured templates, automated calculations, integrated shipping rate comparisons, coupon tracking, and warehouse management features. It eliminates hours of manual setup and provides shopping-specific tools that a generic spreadsheet simply cannot offer — all with a clean, intuitive interface designed for shoppers.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <section id="faq" className="relative py-16 md:py-20 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-4">
            Got Questions?
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
            Frequently Asked <span className="text-primary-600">Questions</span>
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
            Everything you need to know about BaseTao Spreadsheet — answered clearly and honestly.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="bg-background-100 rounded-2xl border border-background-200/60 overflow-hidden"
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between px-5 md:px-6 py-4 md:py-5 text-left cursor-pointer hover:bg-background-200/30 transition-colors"
              >
                <span className="font-semibold text-sm md:text-base text-foreground-900 pr-4">
                  {faq.question}
                </span>
                <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openIndex === idx ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-foreground-500 text-lg"></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === idx ? 'max-h-[500px] pb-5 px-5 md:px-6' : 'max-h-0'
                }`}
              >
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Schema.org FAQ structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}